import { FunctionComponent, useEffect, useMemo, useRef } from "react";
import { OfficeBalance } from "@type/core/offices/offices.types";
import { useTranslation } from "react-i18next";

function formatPrice(price: string) {
  const parts = price.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return parts.join(".");
}

interface FacturationProgressProps {
  facturation?: OfficeBalance;
  threshold?: number;
  className?: string;
}

const FacturationProgress: FunctionComponent<FacturationProgressProps> = ({
  facturation,
  threshold,
  className = "",
}) => {
  const { t } = useTranslation();
  const ratioRef = useRef<HTMLDivElement>(null);
  const inversedRatioRef = useRef<HTMLDivElement>(null);

  const max =
    facturation?.balanceInCents && facturation?.balanceInCents / 100.0;
  const current =
    facturation?.availableCreditInCents &&
    facturation?.availableCreditInCents / 100.0;
  const ratio = useMemo(
    () =>
      current && max ? (max <= 0 ? 0 : Math.floor((current / max) * 100)) : 0,
    [current, max],
  );

  useEffect(() => {
    // Dynamically update the width via the CSSOM
    if (ratioRef.current) {
      ratioRef.current.style.width = `${ratio}%`;
    }
  }, [ratio, ratioRef.current]);

  useEffect(() => {
    // Dynamically update the width via the CSSOM
    if (inversedRatioRef.current) {
      inversedRatioRef.current.style.width = `${100 - ratio}%`;
    }
  }, [ratio, inversedRatioRef.current]);

  return (
    <div className={`app-progress-facturation ${className}`}>
      {max && max > 0 && current !== undefined && threshold !== undefined ? (
        <div>
          <div className="w-100 d-flex align-items-center flex-wrap gap-16">
            <div className="facturation-label flex-grow-1 flex-sm-grow-0">
              0,00 €
            </div>
            <div className="flex-grow-1 facturation-progress-container">
              {ratio >= 0 && (
                <div
                  ref={ratioRef}
                  className={`facturation-progress ${
                    current > threshold / 100 ? "current" : "low"
                  } ${ratio >= 100 ? "unique" : ""}`}
                />
              )}
              {ratio < 100 && (
                <div
                  ref={inversedRatioRef}
                  className={`facturation-progress max ${
                    ratio <= 0 ? "unique" : ""
                  }`}
                />
              )}
            </div>
            <div className="facturation-label flex-grow-1 flex-sm-grow-0 text-right">
              {`${t("offices.balanceAndStatistics.balance")} ${t(
                "offices.balanceAndStatistics.separator",
              )} ${formatPrice(max.toFixed(2)).replace(".", ",")} €`}
            </div>
          </div>
          <div className="d-flex justify-content-end align-items-center gap-16 mt-4">
            <div
              className={`facturation-indicator ${
                current > threshold / 100 ? "current" : " low"
              }`}
            >
              {`${t("offices.balanceAndStatistics.available")} ${t(
                "offices.balanceAndStatistics.separator",
              )} ${formatPrice(current.toFixed(2)).replace(".", ",")} €`}
            </div>
          </div>
        </div>
      ) : (
        <div className="facturation-warning">
          {t("offices.balanceAndStatistics.consumed")}
        </div>
      )}
    </div>
  );
};

export default FacturationProgress;
